import { RecordAtLocation } from '@user/store/map/typings'
import { RECORD_TYPES } from '@user/components/HereMap/FilterChips/constants'
import { PESTPAC_ENTITY_TYPE } from '@user/store/map/pestPacPin'

/**
 * SH-1143 — Given the records that exist at a clicked coordinate (from the unfiltered
 * `getRecordsAtLocation` endpoint) and the user's active map filters, work out which records are
 * hidden by those filters and the minimal filter change needed to reveal ALL of them at once.
 *
 * Mirrors the map's own visibility rules — both the server-side filtering in
 * ContactLocations/SearchArea and the client-side post-filtering in useTileCache:
 *  - Record-type pins (lead/opportunity/draftlead) are hidden when their type is not selected.
 *  - Knock-status pins are hidden when their setting id is not selected.
 *  - A record-type pin that HAS a knock status is hidden when that status isn't selected — including
 *    when NO status is selected at all (the client hides every record with a status via
 *    useTileCache.filterLocationsNoKs). Records without a knock status stay visible.
 *  - A PestPac pin that carries a knock-status badge is hidden when that status isn't selected, the
 *    same as a knock-status pin (SH-1762 — product confirmed PP pins hidden by a knock-status filter
 *    should show the banner). A PestPac pin without a badge stays visible.
 *  - Any record is hidden when "My Records" is on and it is not owned by the current user.
 */

// Reuse the single source of truth for record types instead of re-declaring the list.
const RECORD_TYPE_KEYS: string[] = RECORD_TYPES.map((t) => t.key)

// Warning shown in the map preview tooltip when records at the clicked location are hidden by the
// active filters. Rendered via the existing MapBubbleContentsAlert component.
export const HIDDEN_RECORDS_WARNING =
  'This location has record(s) that are hidden by your filter selections.'

export type ActiveMapFilters = {
  /** Selected record types, lowercased (e.g. ['lead', 'opportunity']). */
  selectedTypes: string[]
  /** Selected knock-status setting ids as strings (e.g. ['3', '5']). */
  selectedKnockStatusIds: string[]
  /** "My Records" filter — when true, only the current user's records are shown. */
  myRecords: boolean
}

export type FilterUpdate = {
  /** '_'-joined entity types for the MapViewFilteredEntityTypes preference. */
  entityTypes: string
  /** '_'-joined knock-status ids, or 'none', for the MapViewFilteredKnockStatuses preference. */
  knockStatusIds: string
  /** 'true' | 'false' for the MapViewFilteredMyRecords preference. */
  myRecords: string
}

export type HiddenByFiltersResult = {
  /** True when at least one record at the coordinate is hidden by the active filters. */
  hiddenExists: boolean
  /** Which preferences actually need to change to reveal the hidden records. */
  changed: {
    entityTypes: boolean
    knockStatusIds: boolean
    myRecords: boolean
  }
  /** The new filter values that reveal every hidden record while leaving unrelated filters intact. */
  update: FilterUpdate
}

const isKnockStatus = (entityType: string | undefined | null) =>
  (entityType ?? '').toLowerCase() === 'knockstatus'

/** How a single record is (or isn't) hidden by the active filters, plus the flags the reveal needs. */
type RecordVisibility = {
  /** True when the record is hidden by any active filter (owner, type, or knock status). */
  hidden: boolean
  ownerHidden: boolean
  typeHidden: boolean
  knockStatusHidden: boolean
  knock: boolean
  isRecordType: boolean
  entityTypeLower: string
  ksId: string | null
}

/**
 * Classify a single record against the active filters. The visibility rules mirror the map's own
 * (server-side SearchArea + client-side useTileCache) — see the module doc-comment above.
 */
function classifyRecordVisibility(
  record: RecordAtLocation,
  selectedTypes: Set<string>,
  selectedKnockStatusIds: Set<string>,
  myRecords: boolean,
  currentUserId: string | null,
): RecordVisibility {
  const knock = isKnockStatus(record.entityType)
  const entityTypeLower = (record.entityType ?? '').toLowerCase()
  const isRecordType = RECORD_TYPE_KEYS.includes(entityTypeLower)
  const isPestPac = entityTypeLower === PESTPAC_ENTITY_TYPE

  // A record hidden by "My Records" when the filter is on and it is not the current user's.
  const ownerHidden =
    myRecords && (record.ownerId == null || record.ownerId !== currentUserId)

  const ksId =
    record.knockStatusSettingsId != null
      ? String(record.knockStatusSettingsId)
      : null

  // Knock-status pin: hidden when its setting id isn't selected (or it has none).
  // Record-type pin: hidden when its own type isn't selected.
  const typeHidden = knock
    ? ksId == null || !selectedKnockStatusIds.has(ksId)
    : isRecordType && !selectedTypes.has(entityTypeLower)

  // A pin that HAS a knock status is hidden when that status isn't selected — but record-type pins
  // and PestPac pins differ on the empty-selection case:
  //  - Record-type pins (Lead/Opp/DraftLead): with NO knock status selected the client hides EVERY
  //    record that has a status (useTileCache.filterLocationsNoKs), so an empty selection counts as
  //    hidden. With a filter active, the server hides those not in the selected list.
  //  - PestPac pins: the server only drops them when a knock-status filter is ACTIVE
  //    (AttachKnockStatusesToPestPacPins, gated on Count > 0) and they skip filterLocationsNoKs — so a
  //    PP pin is hidden only when the filter is non-empty and excludes its status. On an empty
  //    selection a badged PP pin stays visible and must NOT flag the banner.
  // Pins without a knock status are unaffected here (they stay visible via includeWithoutKnockStatus).
  const knockStatusHidden =
    !knock &&
    ksId != null &&
    !selectedKnockStatusIds.has(ksId) &&
    (isRecordType || (isPestPac && selectedKnockStatusIds.size > 0))

  return {
    hidden: ownerHidden || typeHidden || knockStatusHidden,
    ownerHidden,
    typeHidden,
    knockStatusHidden,
    knock,
    isRecordType,
    entityTypeLower,
    ksId,
  }
}

/**
 * SH-1762 — Whether a single record is hidden by the active map filters. Shared with the map's
 * record-routing decision (recordsAtLocationPin) so that a location whose records are ALL hidden
 * falls through to the address bubble and its "records hidden by your filters" banner, instead of
 * opening a record bubble.
 */
export function isRecordHiddenByFilters(
  record: RecordAtLocation,
  filters: ActiveMapFilters,
  currentUserId: string | null,
): boolean {
  return classifyRecordVisibility(
    record,
    new Set(filters.selectedTypes),
    new Set(filters.selectedKnockStatusIds),
    filters.myRecords,
    currentUserId,
  ).hidden
}

export function computeHiddenByFilters(
  records: RecordAtLocation[],
  filters: ActiveMapFilters,
  currentUserId: string | null,
): HiddenByFiltersResult {
  const selectedTypes = new Set(filters.selectedTypes)
  const selectedKnockStatusIds = new Set(filters.selectedKnockStatusIds)

  const nextTypes = new Set(filters.selectedTypes)
  const nextKnockStatusIds = new Set(filters.selectedKnockStatusIds)
  let turnOffMyRecords = false
  let hiddenExists = false

  for (const record of records) {
    const {
      hidden,
      ownerHidden,
      typeHidden,
      knockStatusHidden,
      knock,
      isRecordType,
      entityTypeLower,
      ksId,
    } = classifyRecordVisibility(
      record,
      selectedTypes,
      selectedKnockStatusIds,
      filters.myRecords,
      currentUserId,
    )

    if (!hidden) continue // visible under current filters

    hiddenExists = true

    if (typeHidden) {
      if (knock && ksId != null) {
        nextKnockStatusIds.add(ksId)
      } else if (isRecordType) {
        nextTypes.add(entityTypeLower)
      }
    }
    // A revealed record-type pin must come back WITH its knock-status badge, so re-select its own
    // knock status whenever knockStatusHidden applies. This also covers a pin hidden only by its
    // record type while the knock-status filter is emptied (Scenario B): with no status selected
    // knockStatusHidden is still true, so re-adding the type alone wouldn't restore its badge.
    if (knockStatusHidden && ksId != null) {
      nextKnockStatusIds.add(ksId)
    }
    if (ownerHidden) {
      turnOffMyRecords = true
    }
  }

  const knockStatusIdsValue =
    nextKnockStatusIds.size > 0
      ? Array.from(nextKnockStatusIds).join('_')
      : 'none'

  return {
    hiddenExists,
    changed: {
      entityTypes: nextTypes.size !== selectedTypes.size,
      knockStatusIds: nextKnockStatusIds.size !== selectedKnockStatusIds.size,
      myRecords: turnOffMyRecords && filters.myRecords,
    },
    update: {
      entityTypes: Array.from(nextTypes).filter(Boolean).join('_'),
      knockStatusIds: knockStatusIdsValue,
      myRecords: turnOffMyRecords
        ? 'false'
        : filters.myRecords
          ? 'true'
          : 'false',
    },
  }
}
